import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Facebook, Users, Search, MapPin, ExternalLink, Sparkles, Handshake, Rocket } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { FB_MEMBERS_DISPLAY } from "@/lib/brand";

function formatMembers(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace(".0", "")}k`;
  return v.toLocaleString("fr-FR");
}

function timeSince(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.max(0, Date.now() - d.getTime());
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 30) return `il y a ${days} j`;
    const months = Math.floor(days / 30);
    if (months < 12) return `il y a ${months} mois`;
    return `il y a ${Math.floor(months / 12)} an${months >= 24 ? "s" : ""}`;
  } catch { return ""; }
}

export default function FacebookGroups() {
  const [data, setData] = useState({ items: [], total: 0, total_members: 0, facets: {} });
  const [q, setQ] = useState("");
  const [dep, setDep] = useState("all");
  const [region, setRegion] = useState("all");
  const [loading, setLoading] = useState(true);
  useSeo({
    title: "Nos communautés Facebook locales | PROXIreduc-28",
    description: "Les groupes de bons plans d'Eure-et-Loir au même endroit. Rejoignez la communauté et partagez les meilleures offres locales.",
    canonicalPath: "/groupes-facebook",
    image: "/og-facebook.jpg",
  });

  const load = () => {
    setLoading(true);
    api.get("/facebook-groups").then((r) => setData(r.data || {})).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (data.items || []).filter((g) => {
      if (dep !== "all" && (g.department || "") !== dep) return false;
      if (region !== "all" && (g.region || "") !== region) return false;
      if (qq && !(
        (g.name || "").toLowerCase().includes(qq) ||
        (g.city || "").toLowerCase().includes(qq) ||
        (g.department || "").toLowerCase().includes(qq) ||
        (g.description || "").toLowerCase().includes(qq)
      )) return false;
      return true;
    });
  }, [data.items, q, dep, region]);

  const deps = data?.facets?.departments || [];
  const regions = data?.facets?.regions || [];

  return (
    <div className="bg-[#FAF8F5]" data-testid="fb-groups-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-orange-50 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-12 md:pb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-blue-200 text-xs font-bold uppercase tracking-widest text-blue-700">
              <Facebook size={14}/> Communautés locales · Eure-et-Loir
            </div>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
              Nos <span className="text-blue-700">groupes Facebook</span> locaux
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-700">
              PROXIreduc-28 anime un réseau de communautés Facebook dans chaque ville d'Eure-et-Loir.
              Chaque groupe partage les bons plans, actus et initiatives des commerces et habitants du secteur.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 rounded-2xl bg-white border border-slate-200 px-4 py-2.5 shadow-sm" data-testid="fb-hero-count-groups">
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 grid place-items-center"><Facebook size={16}/></div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Groupes actifs</div>
                  <div className="font-heading text-xl font-extrabold text-slate-900">{data.groups_display || data.total || 0}</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 rounded-2xl bg-white border border-slate-200 px-4 py-2.5 shadow-sm" data-testid="fb-hero-count-members">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center"><Users size={16}/></div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Membres cumulés</div>
                  <div className="font-heading text-xl font-extrabold text-slate-900">{data.members_display || FB_MEMBERS_DISPLAY}</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 rounded-2xl bg-white border border-slate-200 px-4 py-2.5 shadow-sm">
                <div className="w-9 h-9 rounded-lg bg-orange-50 text-[#E05638] grid place-items-center"><MapPin size={16}/></div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Département</div>
                  <div className="font-heading text-base font-extrabold text-slate-900">Eure-et-Loir (28)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTRES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex flex-col md:flex-row gap-2" data-testid="fb-filters">
          <div className="flex items-center gap-2 flex-1 px-3 md:border-r border-slate-200/60">
            <Search className="w-4 h-4 text-slate-400"/>
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Rechercher un groupe, une ville…" className="border-0 shadow-none focus-visible:ring-0 h-11 p-0" data-testid="fb-search"/>
          </div>
          <Select value={dep} onValueChange={setDep}>
            <SelectTrigger className="md:w-56 h-11 border-0 shadow-none" data-testid="fb-filter-dep"><SelectValue placeholder="Département"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les départements</SelectItem>
              {deps.map((d)=>(<SelectItem key={d} value={d}>{d}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="md:w-56 h-11 border-0 shadow-none" data-testid="fb-filter-region"><SelectValue placeholder="Région"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les régions</SelectItem>
              {regions.map((r)=>(<SelectItem key={r} value={r}>{r}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={()=>{setQ(""); setDep("all"); setRegion("all");}} className="h-11" data-testid="fb-reset">Réinitialiser</Button>
        </div>
        <div className="mt-3 text-xs text-slate-500" data-testid="fb-results-count">
          {loading ? "Chargement…" : `${filtered.length} groupe${filtered.length > 1 ? "s" : ""} affiché${filtered.length > 1 ? "s" : ""}`}
        </div>
      </section>

      {/* LISTE DES GROUPES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((g) => (
            <article key={g.id} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col shadow-sm hover:shadow-lg hover:border-blue-300 transition-all" data-testid={`fb-card-${g.id}`}>
              <div className="relative h-36 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 overflow-hidden">
                {g.image_url ? (
                  <img src={g.image_url} alt={g.name} className="absolute inset-0 w-full h-full object-cover"/>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Facebook size={72} className="text-white/20"/>
                  </div>
                )}
                <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 backdrop-blur text-[11px] font-bold text-slate-900">
                  <Users size={11}/> {formatMembers(g.member_count)}
                </div>
                {g.city && (
                  <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/70 backdrop-blur text-[11px] font-bold text-white">
                    <MapPin size={11}/> {g.city}
                  </div>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-heading font-extrabold text-base text-slate-900 leading-tight line-clamp-2">{g.name}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1 line-clamp-3">{g.description}</p>
                <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                  <span>{g.department}</span>
                  {g.member_count_updated_at && <span>MAJ {timeSince(g.member_count_updated_at)}</span>}
                </div>
                <a href={g.url} target="_blank" rel="noreferrer"
                   className="mt-4 inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors"
                   data-testid={`fb-join-${g.id}`}>
                  <Facebook size={15}/> Rejoindre le groupe <ExternalLink size={13}/>
                </a>
              </div>
            </article>
          ))}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-16 bg-white border border-dashed border-slate-200 rounded-2xl">
              Aucun groupe ne correspond à vos filtres.
            </div>
          )}
        </div>
      </section>

      {/* CTA PARTENAIRE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="fb-partner-cta">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white p-6 md:p-12">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-blue-500/20 blur-3xl pointer-events-none max-w-full"/>
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-widest">
                <Sparkles size={12}/> Réservé aux partenaires
              </div>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold">
                Faites parler de votre commerce sur nos {data.total || 0} communautés locales
              </h2>
              <p className="mt-3 text-white/80 text-sm md:text-base">
                Les formules payantes donnent accès à des publications Facebook sur notre réseau de groupes locaux — validées et diffusées par PROXIreduc-28.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/devenir-partenaire">
                  <Button className="bg-white text-slate-900 hover:bg-white/90 h-11 px-6" data-testid="fb-cta-partner">
                    <Rocket size={16} className="mr-2"/> Devenir partenaire
                  </Button>
                </Link>
                <Link to="/bons-plans">
                  <Button variant="outline" className="text-slate-900 h-11 px-6" data-testid="fb-cta-deals">
                    Voir les bons plans
                  </Button>
                </Link>
              </div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: Handshake, label: "Publications validées par notre équipe" },
                { icon: Users, label: `Plus de ${data.members_display || FB_MEMBERS_DISPLAY} membres en cumulé` },
                { icon: MapPin, label: "Une présence dans chaque ville" },
                { icon: Facebook, label: "Diffusion sur les groupes concernés" },
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 bg-white/10 backdrop-blur rounded-xl p-3 border border-white/10">
                  <f.icon size={16} className="text-blue-200 mt-0.5 shrink-0"/>
                  <span className="text-sm">{f.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
